import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LuCreditCard, LuLayoutGrid, LuPlus, LuLayoutDashboard, LuStore, LuList } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import axiosInstance from '../../api/axiosConfig';
import PaymentFormWizard from './PaymentFormWizard';
import PaymentCard from './PaymentCard';
import PaymentCategoryManager from './PaymentCategoryManager';
import CompanyCardManager from './CompanyCardManager';
import RecordPaymentModal from './RecordPaymentModal';
import PaymentsOverview from './PaymentsOverview';
import ServiceAvatar from './ServiceAvatar';
import PaymentDocuments from './PaymentDocuments';
import { CostCentreSelect } from './PaymentPickers';
import {
    PAYMENT_TYPES,
    PAYMENT_STATUSES,
    formatAmount,
    formatDate,
    formatDateTime,
    toDateInput,
    daysUntil,
    cardLabel,
    costCentreLabel
} from './paymentConstants';
import './SubscriptionPayments.css';

const TABS = [
    { key: 'overview', label: 'Overview', Icon: LuLayoutDashboard },
    { key: 'subscriptions', label: 'Services', Icon: LuStore },
    { key: 'payments', label: 'Payments', Icon: LuList }
];

// Date windows for the Payments tab. Sent as calendar days; the server turns
// them into Qatar-day boundaries.
const PERIODS = [
    { key: 'this-month', label: 'This month' },
    { key: 'last-month', label: 'Last month' },
    { key: '3-months', label: 'Last 3 months' },
    { key: 'this-year', label: 'This year' },
    { key: 'all', label: 'All time' }
];

const periodRange = (key) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (key) {
        case 'this-month': return { from: toDateInput(new Date(y, m, 1)), to: toDateInput(new Date(y, m + 1, 0)) };
        case 'last-month': return { from: toDateInput(new Date(y, m - 1, 1)), to: toDateInput(new Date(y, m, 0)) };
        case '3-months': return { from: toDateInput(new Date(y, m - 2, 1)), to: toDateInput(new Date(y, m + 1, 0)) };
        case 'this-year': return { from: toDateInput(new Date(y, 0, 1)), to: toDateInput(new Date(y, 11, 31)) };
        default: return {};
    }
};

// Excel-friendly CSV: quoted cells and a BOM so Arabic names and "›" survive.
const downloadCsv = (filename, rows) => {
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = '﻿' + rows.map(row => row.map(escape).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
};

const SubscriptionPayments = () => {
    const { user } = useAuth();

    const [tab, setTab] = useState('overview');

    const [records, setRecords] = useState([]);
    // Every live record regardless of the Services tab's filters — the overview,
    // the "Record charge" picker and one-click confirm all work from this, so a
    // filter left on in one tab cannot hide a service from another.
    const [liveRecords, setLiveRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [upcoming, setUpcoming] = useState([]);
    const [payments, setPayments] = useState([]);
    const [failedPayments, setFailedPayments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [lookups, setLookups] = useState({ cards: [], costCentres: [], myCostCentre: null });
    const [loading, setLoading] = useState(true);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    // Services tab filters
    const [statusFilter, setStatusFilter] = useState('live');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [autoRenewFilter, setAutoRenewFilter] = useState('');
    const [cardFilter, setCardFilter] = useState('');
    const [centreFilter, setCentreFilter] = useState('');
    const [search, setSearch] = useState('');

    // Payments tab filters
    const [period, setPeriod] = useState('this-month');
    const [payCardFilter, setPayCardFilter] = useState('');
    const [payCentreFilter, setPayCentreFilter] = useState('');
    const [payTypeFilter, setPayTypeFilter] = useState('');
    const [payStatusFilter, setPayStatusFilter] = useState('');
    const [paySearch, setPaySearch] = useState('');

    const [showForm, setShowForm] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [showCategoryManager, setShowCategoryManager] = useState(false);
    const [showCardManager, setShowCardManager] = useState(false);

    // { record } to record a charge, { payment } to edit one, {} to pick a service
    const [payModal, setPayModal] = useState(null);

    const [cancelling, setCancelling] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    const [historyRecord, setHistoryRecord] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyScheduled, setHistoryScheduled] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const isAdmin = user?.role === 'system_admin';
    const canCreate = isAdmin || hasCardAction(userPermissions, 'subscription-payments', 'create');
    const canEdit = isAdmin || hasCardAction(userPermissions, 'subscription-payments', 'edit');
    const canDelete = isAdmin || hasCardAction(userPermissions, 'subscription-payments', 'delete');

    // ---- data ----

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (categoryFilter) params.set('category', categoryFilter);
            if (typeFilter) params.set('paymentType', typeFilter);
            if (autoRenewFilter) params.set('autoRenew', autoRenewFilter);
            if (cardFilter) params.set('card', cardFilter);
            if (centreFilter) params.set('costCentre', centreFilter);
            const query = params.toString();
            const res = await axiosInstance.get(`/api/subscriptions${query ? `?${query}` : ''}`);
            if (res.data.success) setRecords(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payments');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, categoryFilter, typeFilter, autoRenewFilter, cardFilter, centreFilter]);

    const fetchDashboard = useCallback(async () => {
        try {
            const [summaryRes, upcomingRes, liveRes, failedRes] = await Promise.all([
                axiosInstance.get('/api/subscriptions/summary'),
                axiosInstance.get('/api/subscriptions/upcoming?days=60'),
                axiosInstance.get('/api/subscriptions?status=live'),
                axiosInstance.get('/api/subscriptions/payments?status=failed')
            ]);
            if (summaryRes.data.success) setSummary(summaryRes.data.data);
            if (upcomingRes.data.success) setUpcoming(upcomingRes.data.data || []);
            if (liveRes.data.success) setLiveRecords(liveRes.data.data || []);
            if (failedRes.data.success) setFailedPayments(failedRes.data.data || []);
        } catch {
            // The dashboard is supplementary — the lists still work without it
        }
    }, []);

    const fetchPayments = useCallback(async () => {
        setPaymentsLoading(true);
        try {
            const params = new URLSearchParams();
            const range = periodRange(period);
            if (range.from) params.set('from', range.from);
            if (range.to) params.set('to', range.to);
            if (payCardFilter) params.set('card', payCardFilter);
            if (payCentreFilter) params.set('costCentre', payCentreFilter);
            if (payTypeFilter) params.set('paymentType', payTypeFilter);
            if (payStatusFilter) params.set('status', payStatusFilter);
            const res = await axiosInstance.get(`/api/subscriptions/payments?${params.toString()}`);
            if (res.data.success) setPayments(res.data.data || []);
        } catch {
            setPayments([]);
        } finally {
            setPaymentsLoading(false);
        }
    }, [period, payCardFilter, payCentreFilter, payTypeFilter, payStatusFilter]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/payment-categories');
            if (res.data.success) setCategories(res.data.data || []);
        } catch {
            setCategories([]);
        }
    }, []);

    const fetchLookups = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/subscriptions/lookups');
            if (res.data.success) setLookups(res.data.data);
        } catch {
            // Forms still work without cards/branches; the pickers just show empty
        }
    }, []);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);
    useEffect(() => { fetchPayments(); }, [fetchPayments]);
    useEffect(() => { fetchDashboard(); fetchCategories(); fetchLookups(); }, [fetchDashboard, fetchCategories, fetchLookups]);

    const refreshAll = () => { fetchRecords(); fetchDashboard(); fetchPayments(); };

    // A card added from inside a form appears in every picker straight away
    const handleCardCreated = (card) => {
        setLookups(prev => ({ ...prev, cards: [...prev.cards.filter(c => c._id !== card._id), { ...card, label: cardLabel(card) }] }));
    };

    const flash = (message) => {
        setNotice(message);
        setTimeout(() => setNotice(''), 4000);
    };

    // ---- derived ----

    // The card most recently paid with — the sensible default for a new service
    const defaultCardId = useMemo(() => {
        const activeIds = new Set(lookups.cards.filter(c => c.isActive).map(c => String(c._id)));
        const recent = [...payments, ...failedPayments]
            .sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate))
            .find(p => p.card && activeIds.has(String(p.card)));
        if (recent) return String(recent.card);
        const withCard = liveRecords.find(r => r.card && activeIds.has(String(r.card._id || r.card)));
        if (withCard) return String(withCard.card._id || withCard.card);
        return activeIds.size === 1 ? [...activeIds][0] : '';
    }, [lookups.cards, payments, failedPayments, liveRecords]);

    const variableRecords = useMemo(
        () => liveRecords.filter(r => r.paymentType === 'usage').sort((a, b) => a.name.localeCompare(b.name)),
        [liveRecords]
    );

    const visibleRecords = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return records;
        return records.filter(r =>
            [r.name, r.vendor, r.description, r.accountEmail, r.category?.name, r.categoryName,
                r.paymentMethod, r.branchName, r.companyName]
                .filter(Boolean)
                .some(field => field.toLowerCase().includes(term))
        );
    }, [records, search]);

    // Services tab groups by urgency, then by kind — what needs acting on
    // first, then the two ongoing kinds, then anything no longer live.
    const groups = useMemo(() => {
        const attention = [];
        const recurring = [];
        const variable = [];
        const inactive = [];

        for (const record of visibleRecords) {
            const live = ['active', 'trial'].includes(record.status);
            if (!live) {
                inactive.push(record);
            } else if (record.paymentType === 'usage') {
                variable.push(record);
            } else if (record.paymentType === 'onetime') {
                inactive.push(record);
            } else {
                const days = daysUntil(record.nextDueDate);
                if (days !== null && days <= 7) attention.push(record);
                else recurring.push(record);
            }
        }

        return [
            { key: 'attention', title: '⚠️ Needs attention', hint: 'Renewing within 7 days, or waiting to be confirmed', items: attention, tone: 'attention' },
            { key: 'recurring', title: `${PAYMENT_TYPES.subscription.icon} Recurring`, hint: 'Renews on a schedule', items: recurring },
            { key: 'variable', title: `${PAYMENT_TYPES.usage.icon} Pay-as-you-go`, hint: 'Ads, API credits — record each charge', items: variable },
            { key: 'inactive', title: '🗂️ Not running', hint: 'Cancelled, paused, one-time, completed', items: inactive }
        ].filter(g => g.items.length > 0);
    }, [visibleRecords]);

    const visiblePayments = useMemo(() => {
        const term = paySearch.trim().toLowerCase();
        if (!term) return payments;
        return payments.filter(p =>
            [p.subscriptionName, p.reference, p.categoryName, p.cardLabel, p.paymentMethod,
                p.branchName, p.companyName, p.paidByName, p.paidBy?.fullName, p.notes]
                .filter(Boolean)
                .some(field => field.toLowerCase().includes(term))
        );
    }, [payments, paySearch]);

    // Paid totals per currency for whatever is on screen — what a card
    // statement check actually needs. Failed and refunded rows are excluded.
    const paymentTotals = useMemo(() => {
        const totals = {};
        let count = 0;
        for (const p of visiblePayments) {
            if (p.status !== 'paid') continue;
            totals[p.currency || 'QAR'] = (totals[p.currency || 'QAR'] || 0) + (p.amount || 0);
            count++;
        }
        return { totals, count };
    }, [visiblePayments]);

    // ---- actions ----

    const openAddForm = () => { setEditingRecord(null); setFormError(''); setShowForm(true); };
    const openEditForm = (record) => { setEditingRecord(record); setFormError(''); setShowForm(true); };

    const handleSave = async (values) => {
        setFormError('');
        setSaving(true);
        try {
            const payload = {
                ...values,
                amount: values.amount === '' ? null : Number(values.amount),
                reviewFrequencyDays: Number(values.reviewFrequencyDays)
            };
            if (editingRecord) {
                await axiosInstance.put(`/api/subscriptions/${editingRecord._id}`, payload);
            } else {
                await axiosInstance.post('/api/subscriptions', payload);
            }
            setShowForm(false);
            setEditingRecord(null);
            flash(editingRecord ? 'Saved' : `${values.name} added`);
            refreshAll();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        if (!window.confirm(`Delete "${record.name}"? It will be marked deleted (not removed) — payment history is kept.\n\nTo stop a subscription without losing it, use "Cancel subscription" instead.`)) return;
        try {
            await axiosInstance.delete(`/api/subscriptions/${record._id}`);
            refreshAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete');
        }
    };

    const handleCancelConfirm = async () => {
        if (!cancelling) return;
        setSaving(true);
        try {
            await axiosInstance.put(`/api/subscriptions/${cancelling._id}/cancel`, { reason: cancelReason });
            setCancelling(null);
            setCancelReason('');
            refreshAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel');
        } finally {
            setSaving(false);
        }
    };

    const findRecord = (id) =>
        liveRecords.find(r => r._id === id) || records.find(r => r._id === id) || null;

    const openPayModal = (record) => setPayModal({ record });
    const openPayById = (id) => {
        const record = findRecord(id);
        if (record) openPayModal(record);
    };

    // One click for the common case: the auto-renewal went through for the
    // expected amount, on the card on file, on its due date. Anything different
    // (other amount, other card, it failed) goes through "Details…" instead.
    const confirmCharge = async (record, dueDate) => {
        if (!record) return;
        const when = dueDate || record.nextDueDate;
        const card = record.card && typeof record.card === 'object' ? cardLabel(record.card) : record.paymentMethod;
        if (!window.confirm(
            `Confirm ${record.name} was charged?\n\n` +
            `${formatAmount(record.amount, record.currency)} on ${formatDate(when)}${card ? `\nPaid with ${card}` : ''}\n\n` +
            'Different amount or it failed? Cancel and use "Details…" instead.'
        )) return;

        const post = async (allowDuplicate) => {
            const data = new FormData();
            data.append('amount', Number(record.amount));
            data.append('paidDate', new Date(when).toISOString());
            data.append('status', 'paid');
            if (allowDuplicate) data.append('allowDuplicate', 'true');
            await axiosInstance.post(`/api/subscriptions/${record._id}/payments`, data);
        };

        try {
            await post(false);
            flash(`${record.name} confirmed — next renewal scheduled`);
            refreshAll();
        } catch (err) {
            if (err.response?.data?.duplicate && window.confirm(`${err.response.data.message}\n\nRecord it anyway?`)) {
                try {
                    await post(true);
                    refreshAll();
                    return;
                } catch (retryErr) {
                    setError(retryErr.response?.data?.message || 'Failed to confirm the charge');
                    return;
                }
            }
            setError(err.response?.data?.message || 'Failed to confirm the charge');
        }
    };

    const handlePaymentStatusChange = async (payment, status) => {
        const reason = status === 'failed'
            ? window.prompt('What went wrong? (optional — e.g. card declined)') ?? null
            : '';
        if (reason === null) return;
        try {
            await axiosInstance.put(`/api/subscriptions/${payment.subscription?._id || payment.subscription}/payments/${payment._id}`, {
                status,
                failureReason: reason
            });
            refreshAll();
            if (historyRecord) openHistory(historyRecord);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update payment');
        }
    };

    const openHistory = async (record) => {
        setHistoryRecord(record);
        setHistoryRows([]);
        setHistoryScheduled(null);
        setHistoryLoading(true);
        try {
            const res = await axiosInstance.get(`/api/subscriptions/${record._id}/payments`);
            if (res.data.success) {
                setHistoryRows(res.data.data || []);
                setHistoryScheduled(res.data.scheduled || null);
            }
        } catch (err) {
            console.error('Failed to fetch payment history:', err);
        } finally {
            setHistoryLoading(false);
        }
    };

    const openHistoryById = (id) => {
        const record = findRecord(id);
        if (record) openHistory(record);
    };

    const exportPayments = () => {
        const header = ['Date', 'Time', 'Service', 'Type', 'Amount', 'Currency', 'Vendor price', 'Vendor currency',
            'Card / method', 'Company', 'Branch', 'Status', 'Covers renewal', 'Reference', 'Recorded by', 'Department', 'Notes'];
        const rows = visiblePayments.map(p => {
            const d = new Date(p.paidDate);
            return [
                toDateInput(d),
                d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                p.subscriptionName,
                PAYMENT_TYPES[p.paymentType]?.label || 'Recurring',
                Number(p.amount || 0).toFixed(2),
                p.currency || 'QAR',
                p.originalAmount != null ? Number(p.originalAmount).toFixed(2) : '',
                p.originalAmount != null ? p.originalCurrency : '',
                p.cardLabel || p.paymentMethod || '',
                p.companyName || '',
                p.branchName || '',
                PAYMENT_STATUSES[p.status]?.label || p.status,
                p.forDueDate ? toDateInput(p.forDueDate) : '',
                p.reference || '',
                p.paidBy?.fullName || p.paidByName || '',
                p.paidByDepartment || '',
                p.notes || ''
            ];
        });
        const label = PERIODS.find(x => x.key === period)?.label.toLowerCase().replace(/\s+/g, '-') || 'all';
        downloadCsv(`payments-${label}-${toDateInput(new Date())}.csv`, [header, ...rows]);
    };

    const activeCards = lookups.cards.filter(c => c.isActive);

    return (
        <div className="subscription-container">
            <div className="subscription-content">
                <header className="pay-header">
                    <div className="pay-header-title">
                        <span className="pay-header-icon"><LuCreditCard /></span>
                        <div>
                            <h1>Payments</h1>
                            <p>Track subscriptions, service costs, cards and payment history.</p>
                        </div>
                    </div>
                    <div className="pay-header-actions">
                        <button className="pay-btn pay-btn-outline" onClick={() => setShowCardManager(true)}>
                            <LuCreditCard /> Cards
                        </button>
                        {isAdmin && (
                            <button className="pay-btn pay-btn-outline" onClick={() => setShowCategoryManager(true)}>
                                <LuLayoutGrid /> Categories
                            </button>
                        )}
                        {canCreate && (
                            <>
                                <button className="pay-btn pay-btn-green" onClick={() => setPayModal({})}>
                                    <LuPlus /> Record charge
                                </button>
                                <button className="pay-btn pay-btn-maroon" onClick={openAddForm}>
                                    <LuPlus /> Add service
                                </button>
                            </>
                        )}
                    </div>
                </header>

                {error && (
                    <div className="alert alert-error" onClick={() => setError('')} role="button" tabIndex={0}>
                        {error} <span className="form-hint">(click to dismiss)</span>
                    </div>
                )}
                {notice && <div className="alert alert-success">{notice}</div>}
                {activeCards.length === 0 && canCreate && summary && (
                    <div className="setup-hint">
                        💡 Add the company card(s) you pay with — then every service and charge shows which card it hit,
                        and you'll be warned before a card expires.{' '}
                        <button className="link-button" onClick={() => setShowCardManager(true)}>Add a card</button>
                    </div>
                )}

                <div className="pay-tabs" role="tablist">
                    {TABS.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            role="tab"
                            aria-selected={tab === key}
                            className={`pay-tab ${tab === key ? 'pay-tab-active' : ''}`}
                            onClick={() => setTab(key)}
                        >
                            <Icon className="pay-tab-icon" />
                            <span>
                                {label}
                                {key === 'subscriptions' && summary ? ` (${summary.activeSubscriptions + summary.variableCount})` : ''}
                            </span>
                            {key === 'overview' && summary && summary.toConfirmCount + summary.overdueCount > 0 && (
                                <span className="tab-badge">{summary.toConfirmCount + summary.overdueCount}</span>
                            )}
                        </button>
                    ))}
                </div>

                {tab === 'overview' && (
                    <PaymentsOverview
                        onViewAll={(type) => { setStatusFilter('live'); setTypeFilter(type || ''); setTab('subscriptions'); }}
                        summary={summary}
                        upcoming={upcoming}
                        failedPayments={failedPayments}
                        variableRecords={variableRecords}
                        canCreate={canCreate}
                        onPay={openPayById}
                        onConfirm={(id, dueDate) => confirmCharge(findRecord(id), dueDate)}
                        onOpen={openHistoryById}
                        onAdd={openAddForm}
                        onRecordCharge={openPayModal}
                        onManageCards={() => setShowCardManager(true)}
                    />
                )}

                {tab === 'subscriptions' && (
                    <>
                        <div className="toolbar">
                            <input
                                className="search-input"
                                type="search"
                                placeholder="Search name, vendor, card, branch, account email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="live">Active &amp; trials</option>
                                <option value="trial">Trials only</option>
                                <option value="paused">Paused</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="expired">Completed</option>
                                <option value="">All (excl. deleted)</option>
                                <option value="deleted">Deleted</option>
                            </select>
                            <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                <option value="">All types</option>
                                {Object.values(PAYMENT_TYPES).map(t => (
                                    <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                                ))}
                            </select>
                            <select className="filter-select" value={cardFilter} onChange={(e) => setCardFilter(e.target.value)}>
                                <option value="">Any card</option>
                                {lookups.cards.map(c => <option key={c._id} value={c._id}>💳 {cardLabel(c)}{c.isActive ? '' : ' (inactive)'}</option>)}
                                <option value="none">No card linked</option>
                            </select>
                            <CostCentreSelect
                                className="filter-select"
                                costCentres={lookups.costCentres}
                                value={centreFilter}
                                onChange={setCentreFilter}
                                emptyLabel="Any company / branch"
                            />
                            <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                <option value="">All categories</option>
                                {categories.map(c => <option key={c._id} value={c._id}>{c.icon} {c.name}</option>)}
                            </select>
                            <select className="filter-select" value={autoRenewFilter} onChange={(e) => setAutoRenewFilter(e.target.value)}>
                                <option value="">Auto-renew: any</option>
                                <option value="true">Auto-renew ON</option>
                                <option value="false">Auto-renew OFF</option>
                            </select>
                        </div>

                        {loading ? (
                            <div className="loading-state">Loading...</div>
                        ) : groups.length === 0 ? (
                            <div className="empty-state empty-state-lg">
                                <div className="empty-state-icon">🔍</div>
                                <h3>{search ? 'Nothing matches that search' : 'No services here'}</h3>
                                <p>
                                    {search
                                        ? `Nothing matches "${search}". Try a different name, vendor, card or branch.`
                                        : 'Nothing matches the filters above. Try widening them.'}
                                </p>
                            </div>
                        ) : (
                            groups.map(group => (
                                <section key={group.key} className={`pay-group ${group.tone === 'attention' ? 'pay-group-attention' : ''}`}>
                                    <div className="pay-group-header">
                                        <h2 className="pay-group-title">{group.title}</h2>
                                        {group.hint && <span className="pay-group-hint">{group.hint}</span>}
                                        <span className="pay-group-count">{group.items.length}</span>
                                    </div>
                                    <div className="pay-group-items">
                                        {group.items.map(record => (
                                            <PaymentCard
                                                key={record._id}
                                                payment={record}
                                                canCreate={canCreate}
                                                canEdit={canEdit}
                                                canDelete={canDelete}
                                                onPay={openPayModal}
                                                onConfirm={(r) => confirmCharge(r)}
                                                onHistory={openHistory}
                                                onEdit={openEditForm}
                                                onDelete={handleDelete}
                                                onCancel={(r) => { setCancelling(r); setCancelReason(''); }}
                                            />
                                        ))}
                                    </div>
                                </section>
                            ))
                        )}
                    </>
                )}

                {tab === 'payments' && (
                    <>
                        <div className="chip-row period-row">
                            {PERIODS.map(p => (
                                <button
                                    key={p.key}
                                    type="button"
                                    className={`chip ${period === p.key ? 'chip-active' : ''}`}
                                    onClick={() => setPeriod(p.key)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <div className="toolbar">
                            <input
                                className="search-input"
                                type="search"
                                placeholder="Search service, reference, card, branch, person..."
                                value={paySearch}
                                onChange={(e) => setPaySearch(e.target.value)}
                            />
                            <select className="filter-select" value={payCardFilter} onChange={(e) => setPayCardFilter(e.target.value)}>
                                <option value="">Any card</option>
                                {lookups.cards.map(c => <option key={c._id} value={c._id}>💳 {cardLabel(c)}{c.isActive ? '' : ' (inactive)'}</option>)}
                                <option value="none">Not on a card</option>
                            </select>
                            <CostCentreSelect
                                className="filter-select"
                                costCentres={lookups.costCentres}
                                value={payCentreFilter}
                                onChange={setPayCentreFilter}
                                emptyLabel="Any company / branch"
                            />
                            <select className="filter-select" value={payTypeFilter} onChange={(e) => setPayTypeFilter(e.target.value)}>
                                <option value="">All types</option>
                                {Object.values(PAYMENT_TYPES).map(t => (
                                    <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                                ))}
                            </select>
                            <select className="filter-select" value={payStatusFilter} onChange={(e) => setPayStatusFilter(e.target.value)}>
                                <option value="">Any status</option>
                                <option value="paid">Paid</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                            <button className="btn btn-secondary" onClick={exportPayments} disabled={visiblePayments.length === 0}>
                                ⬇ Export CSV
                            </button>
                        </div>

                        {visiblePayments.length > 0 && (
                            <div className="payments-total">
                                <span>
                                    <strong>{paymentTotals.count}</strong> paid
                                    {visiblePayments.length !== paymentTotals.count && ` of ${visiblePayments.length}`}
                                    {' · '}{PERIODS.find(p => p.key === period)?.label}
                                </span>
                                <span className="payments-total-amounts">
                                    {Object.entries(paymentTotals.totals).map(([cur, total]) => (
                                        <strong key={cur}>{formatAmount(total, cur)}</strong>
                                    ))}
                                </span>
                            </div>
                        )}

                        {paymentsLoading ? (
                            <div className="loading-state">Loading payments...</div>
                        ) : visiblePayments.length === 0 ? (
                            <div className="empty-state empty-state-lg">
                                <div className="empty-state-icon">🧾</div>
                                <h3>No payments in this view</h3>
                                <p>Try a longer period or clear the filters. New charges appear here as soon as they are recorded.</p>
                            </div>
                        ) : (
                            <div className="table-scroll card-surface">
                                <table className="history-table">
                                    <thead>
                                        <tr>
                                            <th>Date &amp; time</th>
                                            <th>Service</th>
                                            <th>Amount</th>
                                            <th>Card</th>
                                            <th>Branch</th>
                                            <th>Status</th>
                                            <th>Reference</th>
                                            <th>Documents</th>
                                            <th>By</th>
                                            {canEdit && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiblePayments.map(p => {
                                            const statusMeta = PAYMENT_STATUSES[p.status] || PAYMENT_STATUSES.paid;
                                            return (
                                                <tr key={p._id} className={p.status === 'failed' ? 'row-failed' : ''}>
                                                    <td className="nowrap">{formatDateTime(p.paidDate)}</td>
                                                    <td>
                                                        <div className="table-service">
                                                            <ServiceAvatar record={p.subscription || { name: p.subscriptionName }} size="sm" />
                                                            <div>
                                                                <strong>{p.subscriptionName}</strong>
                                                                <div className="form-hint">
                                                                    {PAYMENT_TYPES[p.paymentType]?.label || 'Recurring'}
                                                                    {p.forDueDate && ` · covers ${formatDate(p.forDueDate)}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell">
                                                        {formatAmount(p.amount, p.currency)}
                                                        {p.originalAmount != null && (
                                                            <div className="form-hint">{formatAmount(p.originalAmount, p.originalCurrency)}</div>
                                                        )}
                                                    </td>
                                                    <td>{p.cardLabel || p.paymentMethod || '—'}</td>
                                                    <td>{costCentreLabel(p) || '—'}</td>
                                                    <td>
                                                        <span className={`status-badge status-${statusMeta.tone}`}>{statusMeta.label}</span>
                                                        {p.failureReason && <div className="form-hint">{p.failureReason}</div>}
                                                    </td>
                                                    <td>{p.reference || '—'}</td>
                                                    <td>
                                                        <PaymentDocuments
                                                            payment={p}
                                                            subscriptionId={p.subscription?._id || p.subscription}
                                                            canEdit={canEdit}
                                                            onChanged={refreshAll}
                                                        />
                                                    </td>
                                                    <td>
                                                        {p.paidBy?.fullName || p.paidByName || '—'}
                                                        {p.paidByDepartment && <div className="form-hint">{p.paidByDepartment}</div>}
                                                    </td>
                                                    {canEdit && (
                                                        <td className="nowrap">
                                                            <button className="btn btn-sm btn-ghost" onClick={() => setPayModal({ payment: p })}>Edit</button>
                                                            {p.status === 'paid' ? (
                                                                <button className="btn btn-sm btn-ghost" onClick={() => handlePaymentStatusChange(p, 'failed')}>
                                                                    Mark failed
                                                                </button>
                                                            ) : p.status === 'failed' ? (
                                                                <button className="btn btn-sm btn-ghost" onClick={() => handlePaymentStatusChange(p, 'paid')}>
                                                                    Mark paid
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {showForm && (
                    <PaymentFormWizard
                        editing={editingRecord}
                        categories={categories}
                        lookups={lookups}
                        defaultCardId={defaultCardId}
                        defaultEmail={user?.email || ''}
                        saving={saving}
                        error={formError}
                        onSubmit={handleSave}
                        onCardCreated={handleCardCreated}
                        onClose={() => { setShowForm(false); setEditingRecord(null); }}
                    />
                )}

                {payModal && (
                    <RecordPaymentModal
                        record={payModal.record}
                        payment={payModal.payment}
                        records={liveRecords}
                        lookups={lookups}
                        defaultCardId={defaultCardId}
                        canAddCard={canCreate}
                        onCardCreated={handleCardCreated}
                        onClose={() => setPayModal(null)}
                        onSaved={() => {
                            const editingPayment = Boolean(payModal.payment);
                            setPayModal(null);
                            flash(editingPayment ? 'Payment updated' : 'Payment recorded');
                            refreshAll();
                            if (historyRecord) openHistory(historyRecord);
                        }}
                    />
                )}

                {showCategoryManager && (
                    <PaymentCategoryManager
                        onClose={() => setShowCategoryManager(false)}
                        onChanged={() => { fetchCategories(); fetchRecords(); }}
                    />
                )}

                {showCardManager && (
                    <CompanyCardManager
                        canCreate={canCreate}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        onClose={() => setShowCardManager(false)}
                        onChanged={() => { fetchLookups(); refreshAll(); }}
                    />
                )}

                {/* Cancel subscription — deliberately separate from delete */}
                {cancelling && (
                    <div className="modal-overlay" onClick={() => setCancelling(null)}>
                        <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Cancel {cancelling.name}?</h2>
                                <button className="modal-close" onClick={() => setCancelling(null)}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="summary-box">
                                    <span className="summary-box-icon">🛑</span>
                                    <span>
                                        It stops renewing and leaves upcoming payments. The record and every payment
                                        ever made against it are kept — this is not a delete. Remember to cancel it at
                                        the vendor too{cancelling.billingUrl ? ' (billing page link below)' : ''}.
                                    </span>
                                </div>
                                {cancelling.billingUrl && (
                                    <p>
                                        <a href={cancelling.billingUrl} target="_blank" rel="noopener noreferrer">
                                            Open {cancelling.name} billing page ↗
                                        </a>
                                    </p>
                                )}
                                <div className="form-group">
                                    <label className="form-label">Reason <span className="form-hint">(optional)</span></label>
                                    <input
                                        className="form-input"
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="e.g. switched to annual plan"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setCancelling(null)}>Keep it</button>
                                <button className="btn btn-warning" onClick={handleCancelConfirm} disabled={saving}>
                                    {saving ? 'Cancelling...' : 'Cancel subscription'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* History */}
                {historyRecord && (
                    <div className="modal-overlay" onClick={() => setHistoryRecord(null)}>
                        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 className="history-title">
                                    <ServiceAvatar record={historyRecord} size="sm" /> {historyRecord.name}
                                </h2>
                                <button className="modal-close" onClick={() => setHistoryRecord(null)}>×</button>
                            </div>
                            <div className="modal-body">
                                {historyLoading ? (
                                    <div className="loading-state">Loading history...</div>
                                ) : (
                                    <>
                                        <div className="history-total">
                                            {historyRows.filter(r => r.status === 'paid').length} payment(s) ·
                                            {' '}{formatAmount(
                                                historyRows.filter(r => r.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0),
                                                historyRecord.currency
                                            )} paid in total
                                            {historyRecord.billingUrl && (
                                                <a className="history-billing-link" href={historyRecord.billingUrl} target="_blank" rel="noopener noreferrer">
                                                    Billing page ↗
                                                </a>
                                            )}
                                        </div>
                                        <div className="table-scroll">
                                            <table className="history-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date &amp; time</th>
                                                        <th>Covers</th>
                                                        <th>Amount</th>
                                                        <th>Card</th>
                                                        <th>Branch</th>
                                                        <th>Status</th>
                                                        <th>Reference</th>
                                                        <th>Documents</th>
                                                        <th>By</th>
                                                        {canEdit && <th></th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Derived, never stored — this is what WILL be charged,
                                                        not money that has already moved. */}
                                                    {historyScheduled && (
                                                        <tr className="row-scheduled">
                                                            <td>{formatDate(historyScheduled.dueDate)}</td>
                                                            <td>—</td>
                                                            <td className="amount-cell">
                                                                {formatAmount(historyScheduled.amount, historyScheduled.currency)}
                                                            </td>
                                                            <td colSpan={2}>{historyRecord.paymentMethod || '—'}</td>
                                                            <td><span className="status-badge status-info">Scheduled</span></td>
                                                            <td colSpan={canEdit ? 4 : 3} className="form-hint">
                                                                {historyScheduled.autoRenew
                                                                    ? 'Will be charged automatically'
                                                                    : 'Needs to be paid manually'}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {historyRows.length === 0 && !historyScheduled && (
                                                        <tr>
                                                            <td colSpan={canEdit ? 10 : 9} className="empty-cell">No payment history yet.</td>
                                                        </tr>
                                                    )}
                                                    {historyRows.map(row => {
                                                        const statusMeta = PAYMENT_STATUSES[row.status] || PAYMENT_STATUSES.paid;
                                                        return (
                                                            <tr key={row._id} className={row.status === 'failed' ? 'row-failed' : ''}>
                                                                <td className="nowrap">{formatDateTime(row.paidDate)}</td>
                                                                <td>{row.forDueDate ? formatDate(row.forDueDate) : '—'}</td>
                                                                <td className="amount-cell">
                                                                    {formatAmount(row.amount, row.currency)}
                                                                    {row.originalAmount != null && (
                                                                        <div className="form-hint">{formatAmount(row.originalAmount, row.originalCurrency)}</div>
                                                                    )}
                                                                </td>
                                                                <td>{row.cardLabel || row.paymentMethod || '—'}</td>
                                                                <td>{costCentreLabel(row) || '—'}</td>
                                                                <td>
                                                                    <span className={`status-badge status-${statusMeta.tone}`}>{statusMeta.label}</span>
                                                                    {row.failureReason && <div className="form-hint">{row.failureReason}</div>}
                                                                </td>
                                                                <td>{row.reference || '—'}</td>
                                                                <td>
                                                                    <PaymentDocuments
                                                                        payment={row}
                                                                        subscriptionId={historyRecord._id}
                                                                        canEdit={canEdit}
                                                                        onChanged={() => { refreshAll(); openHistory(historyRecord); }}
                                                                    />
                                                                </td>
                                                                <td>{row.paidBy?.fullName || row.paidByName || '—'}</td>
                                                                {canEdit && (
                                                                    <td className="nowrap">
                                                                        <button className="btn btn-sm btn-ghost"
                                                                            onClick={() => setPayModal({ payment: { ...row, subscription: historyRecord._id } })}>
                                                                            Edit
                                                                        </button>
                                                                        {row.status === 'paid' ? (
                                                                            <button className="btn btn-sm btn-ghost" onClick={() => handlePaymentStatusChange({ ...row, subscription: historyRecord._id }, 'failed')}>
                                                                                Mark failed
                                                                            </button>
                                                                        ) : row.status === 'failed' ? (
                                                                            <button className="btn btn-sm btn-ghost" onClick={() => handlePaymentStatusChange({ ...row, subscription: historyRecord._id }, 'paid')}>
                                                                                Mark paid
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                {canCreate && ['active', 'trial'].includes(historyRecord.status) && historyRecord.paymentType !== 'onetime' && (
                                    <button className="btn btn-success" onClick={() => openPayModal(historyRecord)}>+ Record charge</button>
                                )}
                                <button className="btn btn-secondary" onClick={() => setHistoryRecord(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPayments;
